import { useRef, useState } from "react";
import { Camera, CheckCircle2, FileText, Loader2, Plus, Trash2, TriangleAlert, Upload } from "lucide-react";
import AppShell from "../components/AppShell";
import DropZone from "../components/DropZone";
import { api } from "../lib/api";

const CATEGORIES_INCOME = ["Servicios", "Producto", "Consultoría", "Sueldo", "Anticipo", "Otros"];
const CATEGORIES_EXPENSE = ["Transporte", "Comida", "Compras", "Software", "Impuestos", "Inventario", "Renta", "Nómina", "Publicidad", "Servicios", "Otras"];
const PAYMENT_METHODS = ["Transferencia", "Depósito", "Efectivo", "Tarjeta", "Otro"];

const money = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 2 });

export default function ImportPage() {
  const [transactions, setTransactions] = useState([]);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [duplicateStrategy, setDuplicateStrategy] = useState("skip");
  const imgInput = useRef(null);
  const pdfInput = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const body = new FormData();
      body.append("file", file);
      const response = await api.post("/bank-scanner/analyze", body, { withCredentials: true });
      setTransactions(response.data.transactions || []);
      setAccount(response.data.account || null);
      if (!response.data.transactions?.length) {
        setError("No detectamos movimientos en este archivo. Intenta con una imagen más clara o un PDF oficial.");
      }
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos analizar el archivo.");
    } finally {
      setLoading(false);
      if (imgInput.current) imgInput.current.value = "";
      if (pdfInput.current) pdfInput.current.value = "";
    }
  };

  const updateTx = (id, field, value) => setTransactions((current) => current.map((tx) => (tx.id === id ? { ...tx, [field]: value } : tx)));
  const removeTx = (id) => setTransactions((current) => current.filter((tx) => tx.id !== id));
  const addTx = () => setTransactions((current) => [
    ...current,
    { id: `manual-${Date.now()}`, date: new Date().toISOString().slice(0, 10), description: "", amount: 0, currency: "MXN", type: "expense", category: "Otras", payment_method: "Transferencia", reference: null, possible_duplicate: null },
  ]);

  const save = async (only = null) => {
    const payload = only ? [transactions.find((tx) => tx.id === only)] : transactions;
    setSaving(true);
    setError("");
    try {
      const response = await api.post("/bank-scanner/save", { transactions: payload, duplicate_strategy: duplicateStrategy }, { withCredentials: true });
      setNotice(`Guardados: ${response.data.saved}. Omitidos: ${response.data.skipped}. Reemplazados: ${response.data.replaced}.`);
      if (only) setTransactions((current) => current.filter((tx) => tx.id !== only));
      else setTransactions([]);
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos guardar los movimientos.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell>
      <main className="page import-page">
        <header className="expenses-head">
          <div>
            <small className="eyebrow">IMPORTAR MOVIMIENTOS</small>
            <h1>Estados de cuenta, <em>al vuelo.</em></h1>
            <p style={{ color: "#5a666b", margin: "6px 0 0", maxWidth: 620 }}>Sube una captura, foto o PDF de tu banco. La IA extrae cada movimiento y tú decides qué guardar.</p>
          </div>
        </header>

        {notice && <p className="expenses-notice" data-testid="import-notice">{notice}</p>}
        {error && <p className="expenses-error" data-testid="import-error">{error}</p>}

        <section className="import-actions" data-testid="import-actions">
          <DropZone
            onFiles={(files) => upload(files[0])}
            accept="image/*,application/pdf"
            multiple={false}
            disabled={loading}
            testid="import-dropzone"
            hint="PDF, imagen o captura · máx. 15 MB"
          />
          <div className="import-legacy-buttons">
            <button type="button" className="expenses-scan-button" data-testid="import-image-button" disabled={loading} onClick={() => imgInput.current?.click()}>
              <Camera size={18} /> Cámara
            </button>
            <button type="button" className="expenses-scan-button" data-testid="import-pdf-button" disabled={loading} onClick={() => pdfInput.current?.click()}>
              <FileText size={18} /> Estado de cuenta PDF
            </button>
          </div>
          <input ref={imgInput} data-testid="import-image-input" type="file" accept="image/*" style={{ display: "none" }} onChange={(event) => upload(event.target.files?.[0])} />
          <input ref={pdfInput} data-testid="import-pdf-input" type="file" accept="application/pdf" style={{ display: "none" }} onChange={(event) => upload(event.target.files?.[0])} />
        </section>

        {loading && (
          <div className="import-loading" data-testid="import-loading">
            <Loader2 className="spin" size={22} />
            <span>Analizando movimientos...</span>
          </div>
        )}

        {account && (account.bank || account.holder || account.period_start) && (
          <section className="import-account" data-testid="import-account">
            {account.bank && <div><small>BANCO</small><b>{account.bank}</b></div>}
            {account.holder && <div><small>TITULAR</small><b>{account.holder}</b></div>}
            {account.account_number_masked && <div><small>CUENTA</small><b>{account.account_number_masked}</b></div>}
            {(account.period_start || account.period_end) && <div><small>PERIODO</small><b>{account.period_start || ""} → {account.period_end || ""}</b></div>}
          </section>
        )}

        {transactions.length > 0 && (
          <>
            <div className="import-controls" data-testid="import-controls">
              <div>
                <b>{transactions.length}</b> movimiento{transactions.length === 1 ? "" : "s"} detectado{transactions.length === 1 ? "" : "s"}
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                Si hay duplicados:
                <select data-testid="import-duplicate-strategy" value={duplicateStrategy} onChange={(event) => setDuplicateStrategy(event.target.value)} className="expenses-select" style={{ padding: "6px 10px" }}>
                  <option value="skip">Omitir</option>
                  <option value="replace">Reemplazar existente</option>
                  <option value="keep_both">Guardar de todos modos</option>
                </select>
              </label>
              <button type="button" data-testid="import-add-tx-button" onClick={addTx} className="expenses-scan-button" style={{ padding: "8px 14px" }}>
                <Plus size={15} />
                Añadir
              </button>
              <button type="button" data-testid="import-save-all-button" onClick={() => save(null)} disabled={saving} className="expenses-add-button">
                <CheckCircle2 size={17} />
                {saving ? "Guardando..." : "Guardar movimientos"}
              </button>
            </div>

            <div className="import-tx-list" data-testid="import-tx-list">
              {transactions.map((tx) => (
                <article key={tx.id} className={`import-tx ${tx.possible_duplicate ? "duplicate" : ""}`} data-testid={`import-tx-${tx.id}`}>
                  {tx.possible_duplicate && (
                    <div className="import-tx-duplicate" data-testid={`import-tx-duplicate-${tx.id}`}>
                      <TriangleAlert size={14} />
                      Posible duplicado (mismo monto y fecha ya registrado)
                    </div>
                  )}
                  <div className="import-tx-grid">
                    <label>Fecha
                      <input type="date" data-testid={`import-tx-date-${tx.id}`} value={tx.date} onChange={(event) => updateTx(tx.id, "date", event.target.value)} />
                    </label>
                    <label>Concepto
                      <input data-testid={`import-tx-desc-${tx.id}`} value={tx.description} onChange={(event) => updateTx(tx.id, "description", event.target.value)} />
                    </label>
                    <label>Monto
                      <input type="number" step="0.01" min="0" data-testid={`import-tx-amount-${tx.id}`} value={tx.amount} onChange={(event) => updateTx(tx.id, "amount", Number(event.target.value))} />
                    </label>
                    <label>Tipo
                      <select data-testid={`import-tx-type-${tx.id}`} value={tx.type} onChange={(event) => updateTx(tx.id, "type", event.target.value)}>
                        <option value="income">Ingreso</option>
                        <option value="expense">Egreso</option>
                      </select>
                    </label>
                    <label>Categoría
                      <select data-testid={`import-tx-category-${tx.id}`} value={tx.category} onChange={(event) => updateTx(tx.id, "category", event.target.value)}>
                        {(tx.type === "income" ? CATEGORIES_INCOME : CATEGORIES_EXPENSE).map((cat) => <option key={cat}>{cat}</option>)}
                      </select>
                    </label>
                    <label>Método
                      <select data-testid={`import-tx-method-${tx.id}`} value={tx.payment_method} onChange={(event) => updateTx(tx.id, "payment_method", event.target.value)}>
                        {PAYMENT_METHODS.map((option) => <option key={option}>{option}</option>)}
                      </select>
                    </label>
                  </div>
                  <div className="import-tx-actions">
                    <b className={tx.type === "income" ? "income" : "expense"}>{tx.type === "income" ? "+" : "−"} {money.format(tx.amount)}</b>
                    <button type="button" data-testid={`import-tx-save-${tx.id}`} onClick={() => save(tx.id)} disabled={saving} className="expenses-scan-button" style={{ padding: "6px 12px", fontSize: 13 }}>Guardar</button>
                    <button type="button" data-testid={`import-tx-delete-${tx.id}`} onClick={() => removeTx(tx.id)} aria-label="Eliminar movimiento" className="import-tx-delete"><Trash2 size={15} /></button>
                  </div>
                </article>
              ))}
            </div>
          </>
        )}

        {!transactions.length && !loading && (
          <div className="expenses-empty" data-testid="import-empty-state">
            <span><Upload size={22} /></span>
            <h2>Todavía no importas ningún movimiento.</h2>
            <p>Elige una captura o un PDF y deja que la IA haga el trabajo.</p>
          </div>
        )}
      </main>
    </AppShell>
  );
}
